/**
 * Service for managing cached impersonation sessions in localStorage
 * Stores multiple sessions indexed by sliceId for quick switching
 */

const SESSIONS_KEY = 'impersonation_sessions';
const CURRENT_SLICE_KEY = 'current_impersonation_slice';

export const sessionCache = {
  /**
   * Store a session in the cache
   * @param {Object} session - Session object with token, targetSliceId, etc.
   */
  storeSession(session) {
    try {
      const sessions = this.getAllSessions();
      sessions[session.targetSliceId] = {
        token: session.token,
        targetSliceId: session.targetSliceId,
        targetSliceName: session.targetSliceName,
        targetCustomerName: session.targetCustomerName,
        expiresAt: session.expiresAt,
        reason: session.reason,
        cachedAt: new Date().toISOString()
      };
      localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
    } catch (error) {
      console.error('Failed to store session:', error);
    }
  },

  /**
   * Get a specific session by sliceId
   * @param {string} sliceId
   * @returns {Object|null}
   */
  getSession(sliceId) {
    try {
      const sessions = this.getAllSessions();
      const session = sessions[sliceId];
      
      // Check if session has expired
      if (session && session.expiresAt) {
        const expiry = new Date(session.expiresAt);
        if (expiry <= new Date()) {
          this.removeSession(sliceId);
          return null;
        }
      }
      
      return session || null;
    } catch (error) {
      console.error('Failed to get session:', error);
      return null;
    }
  },

  /**
   * Get all cached sessions
   * @returns {Object} Map of sliceId to session
   */
  getAllSessions() {
    try {
      const data = localStorage.getItem(SESSIONS_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Failed to get all sessions:', error);
      return {};
    }
  },

  /**
   * Remove a specific session
   * @param {string} sliceId
   */
  removeSession(sliceId) {
    try {
      const sessions = this.getAllSessions();
      delete sessions[sliceId];
      localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
      
      // If this was the current session, clear it
      if (this.getCurrentSliceId() === sliceId) {
        this.clearCurrent();
      }
    } catch (error) {
      console.error('Failed to remove session:', error);
    }
  },

  /**
   * Clear all sessions
   */
  clearAll() {
    try {
      localStorage.removeItem(SESSIONS_KEY);
      localStorage.removeItem(CURRENT_SLICE_KEY);
    } catch (error) {
      console.error('Failed to clear sessions:', error);
    }
  },

  /**
   * Set the current active slice
   * @param {string} sliceId
   */
  setCurrentSlice(sliceId) {
    try {
      localStorage.setItem(CURRENT_SLICE_KEY, sliceId);
    } catch (error) {
      console.error('Failed to set current slice:', error);
    }
  },

  /**
   * Get the current active slice ID
   * @returns {string|null}
   */
  getCurrentSliceId() {
    try {
      return localStorage.getItem(CURRENT_SLICE_KEY);
    } catch (error) {
      console.error('Failed to get current slice:', error);
      return null;
    }
  },

  /**
   * Get the current active session
   * @returns {Object|null}
   */
  getCurrentSession() {
    const sliceId = this.getCurrentSliceId();
    return sliceId ? this.getSession(sliceId) : null;
  },

  /**
   * Clear current session reference
   */
  clearCurrent() {
    try {
      localStorage.removeItem(CURRENT_SLICE_KEY);
    } catch (error) {
      console.error('Failed to clear current session:', error);
    }
  },

  /**
   * Sync sessions from backend response
   * Updates cache with fresh data from server
   * @param {Array} sessions - Array of session objects from backend
   */
  syncFromBackend(sessions) {
    try {
      const cached = this.getAllSessions();
      const currentSliceId = this.getCurrentSliceId();
      
      // Create a set of active slice IDs from backend
      const activeSliceIds = new Set(sessions.map(s => s.targetSliceId));
      
      // Remove expired/ended sessions that are no longer in backend response
      Object.keys(cached).forEach(sliceId => {
        if (!activeSliceIds.has(sliceId)) {
          delete cached[sliceId];
        }
      });
      
      // Update/add sessions from backend
      sessions.forEach(session => {
        cached[session.targetSliceId] = {
          token: session.token,
          targetSliceId: session.targetSliceId,
          targetSliceName: session.targetSliceName,
          targetCustomerName: session.targetCustomerName,
          expiresAt: session.expiresAt,
          reason: session.reason,
          cachedAt: new Date().toISOString()
        };
      });
      
      localStorage.setItem(SESSIONS_KEY, JSON.stringify(cached));
      
      // If current slice is no longer valid, clear it
      if (currentSliceId && !activeSliceIds.has(currentSliceId)) {
        this.clearCurrent();
      }
    } catch (error) {
      console.error('Failed to sync sessions:', error);
    }
  }
};